import { RliPreHook } from '@rocketlane/rli-internal-sdk';
import { stringify } from 'query-string';
import type { FileAttachment } from 'api/attachments';
import type { GetConversationsResponse } from 'api/conversations';
import { HttpClient } from 'api/http-client';
import { InheritableFieldsInfo } from 'api/tasks';
import type { EntityToApprovalMapping } from 'components/organisms/invoice-settings/types';
import { ExpenseEvents } from 'events/expenses';
import { fieldAccessMetaToBase3 } from 'utils/field-access';
import { ExpenseAccess, ExpenseAccessFlag } from '../types';
import type { Expense, ExpenseResponse, ExtractionResult } from './types';

const BASE_URL = '/expenses';

class ExpenseApiClient extends HttpClient {
  public getExpenseById = async (
    expenseId: number,
  ): Promise<ExpenseResponse> => {
    const response: ExpenseResponse = await this.get(
      `${BASE_URL}/${expenseId}`,
    );

    return {
      ...response,
      fieldsAccessMetaInBase3: response.fieldsAccessMeta
        ? fieldAccessMetaToBase3(response.fieldsAccessMeta)
        : undefined,
    };
  };

  public getExpensesByIds = (
    expenseIds: number[],
  ): Promise<{ [expenseId: number]: ExpenseResponse }> => {
    return this.get(`${BASE_URL}/by-ids?expenseIds=${expenseIds}`);
  };

  @RliPreHook({
    event: ExpenseEvents.ExpenseCreate,
    dataGetter: (expense) => ({
      expense,
    }),
  })
  public async createExpense(
    expense: Omit<Expense, 'expenseId'>,
  ): Promise<Expense> {
    return this.post(BASE_URL, expense);
  }

  @RliPreHook({
    event: ExpenseEvents.ExpenseUpdate,
    dataGetter: (expenseId, expense) => ({
      expenseId,
      expense,
    }),
  })
  public async updateExpense(
    expenseId: number,
    expense: Partial<Expense>,
  ): Promise<ExpenseResponse> {
    return this.put(`${BASE_URL}/${expenseId}`, expense);
  }

  @RliPreHook({
    event: ExpenseEvents.ExpenseDelete,
    dataGetter: (expenseId) => ({
      expenseId,
    }),
  })
  public async deleteExpense(expenseId: number): Promise<void> {
    return this.delete(`${BASE_URL}/${expenseId}`);
  }

  public extractExpenseDetails = (
    expenseDocuments: Array<Pick<FileAttachment, 'downloadUrl'>>,
  ): Promise<ExtractionResult> => {
    return this.post(`${BASE_URL}/extract`, expenseDocuments);
  };

  public updateUserStatusForExpense = (
    expenseId: number,
    entityToApprovalMappingId: number,
    entityToApprovalMapping: Pick<EntityToApprovalMapping, 'approvalStatus'>,
  ): Promise<ExpenseResponse> => {
    return this.put(
      `${BASE_URL}/${expenseId}/approvals/${entityToApprovalMappingId}`,
      entityToApprovalMapping,
    );
  };

  public overrideExpenseStatus = (
    expenseId: number,
    approvalWorkflowLevelId: number,
    entityToApprovalMapping: Pick<EntityToApprovalMapping, 'approvalStatus'>,
  ): Promise<ExpenseResponse> => {
    return this.put(
      `${BASE_URL}/${expenseId}/approvals/level/${approvalWorkflowLevelId}/update-status`,
      entityToApprovalMapping,
    );
  };

  public forceApprovalOnExpense = (
    expenseId: number,
    entityToApprovalMapping: Pick<EntityToApprovalMapping, 'approvalStatus'>,
  ): Promise<ExpenseResponse> => {
    return this.put(
      `${BASE_URL}/${expenseId}/force-approval`,
      entityToApprovalMapping,
    );
  };

  public getExpenseAuditLogs = (
    expenseId: number,
  ): Promise<GetConversationsResponse> => {
    return this.get(`${BASE_URL}/${expenseId}/audit-logs`);
  };

  public getAccessibleProjects = (
    action: 'CREATE' | 'READ',
  ): Promise<number[]> => {
    return this.get(`${BASE_URL}/project-access?action=${action}`);
  };

  public getAccessibleUsers = (
    action: 'CREATE' | 'READ',
    projectId?: number,
  ): Promise<number[]> => {
    return this.get(
      `${BASE_URL}/user-access?action=${action}${
        projectId ? `&projectId=${projectId}` : ''
      }`,
    );
  };

  public getExpenseAccess = (
    projectId?: number,
    userId?: number,
  ): Promise<ExpenseAccess> => {
    return this.get<ExpenseAccess>(
      `${BASE_URL}/access?${stringify(
        {
          projectId,
          userId,
        },
        { skipNull: true },
      )}`,
    ).then((access) => {
      return {
        ...access,
        fieldAccessMetaInBase3: fieldAccessMetaToBase3(access.fieldsAccessMeta),
      };
    });
  };

  public getExpenseAccessFlagsOrder = (): Promise<
    Record<ExpenseAccessFlag, number>
  > => {
    return this.get(`${BASE_URL}/access-flags`);
  };

  public getExpenseInheritableFieldsInfo = async (
    projectIds: Set<number>,
    taskIds: Set<number>,
    userIds: Set<number>,
  ): Promise<InheritableFieldsInfo> => {
    return this.get(
      `${BASE_URL}/inheritable-field-values?${stringify(
        {
          projectIds: Array.from(projectIds).join(','),
          taskIds: Array.from(taskIds).join(','),
          userIds: Array.from(userIds).join(','),
        },
        {
          skipNull: true,
          skipEmptyString: false,
        },
      )}`,
    );
  };
}

export const ExpenseApi = new ExpenseApiClient();
